/**
 * Roth conversion explorer card (spec §10): the candidate table (not a single
 * recommended number), the year-by-year schedule with marginal rates, the
 * breakeven year, and the ACA collision warning — surfaced prominently, not
 * buried.
 */
import { Fragment, useMemo, useState } from 'react';
import type { Plan } from '../model/types';
import { exploreRothConversions, type RothOutcome } from '../engine/rothExplorer';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  plan: Plan;
  onChange: (updated: Plan) => void;
}

type Objective = 'estate' | 'taxes' | 'networth';

export function RothExplorerCard({ plan, onChange }: Props) {
  const [heirRate, setHeirRate] = useState(0.24);
  const [objective, setObjective] = useState<Objective>('estate');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const res = useMemo(
    () => exploreRothConversions(plan, { heirMarginalRate: heirRate }),
    [plan, heirRate],
  );
  if (!res.hasPretax) return null;

  // Conversions need somewhere to land. Without a Roth account every strategy
  // silently no-ops — say so and offer the one-click fix instead.
  if (!res.hasRothDestination) {
    return (
      <section className="card" aria-label="Roth conversion explorer">
        <h2 className="section-title">Roth conversions: is prepaying tax worth it?</h2>
        <div className="warnbox">
          <strong>No Roth account to convert into.</strong> Your plan has pre-tax money but no
          Roth IRA, so every conversion strategy would have nowhere to put the converted
          dollars — the numbers below would not move. Add one (a $0 balance is fine; you would
          open it when you first convert) and the explorer comes to life.
        </div>
        <button
          className="btn primary"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => {
            const next = structuredClone(plan);
            next.accounts.push({
              id: `acct_roth_${next.accounts.length + 1}`,
              ownerId: next.household.people[0]!.id,
              type: 'roth_ira',
              label: 'Roth IRA (for conversions)',
              balance: 0,
              asOfDate: '2026-08-04',
              allocation: { equityPct: 80, bondPct: 15, cashPct: 5 },
            });
            onChange(next);
          }}
        >
          Add a Roth IRA to receive conversions
        </button>
      </section>
    );
  }

  const all = [res.baseline, ...res.candidates];
  const score = (c: RothOutcome) =>
    objective === 'estate' ? c.estateValueAfterHeirTax
    : objective === 'taxes' ? -c.lifetimeTaxNominal
    : c.endingNetWorth;
  const best = all.reduce((b, c) => (score(c) > score(b) ? c : b), all[0]!);

  const appliedKey =
    plan.decisions.rothConversionRule == null
      ? 'none'
      : all.find(
          (c) =>
            c.rule?.type === plan.decisions.rothConversionRule?.type &&
            c.rule?.target === plan.decisions.rothConversionRule?.target,
        )?.key ?? null;

  function apply(c: RothOutcome) {
    const next = structuredClone(plan);
    next.decisions.rothConversionRule = c.rule ? { ...c.rule } : undefined;
    onChange(next);
  }

  return (
    <section className="card" aria-label="Roth conversion explorer">
      <h2 className="section-title">
        Roth conversions: is prepaying tax worth it?
        <span className="note">
          conversion window {res.windowStart}–{res.windowEnd} — the gap years between retirement and RMDs
        </span>
      </h2>

      <div className="add-row" style={{ marginBottom: 'var(--space-3)' }}>
        <span className="seg" role="group" aria-label="Objective">
          <button aria-pressed={objective === 'estate'} onClick={() => setObjective('estate')}>
            Estate value
          </button>
          <button aria-pressed={objective === 'taxes'} onClick={() => setObjective('taxes')}>
            Lifetime taxes
          </button>
          <button aria-pressed={objective === 'networth'} onClick={() => setObjective('networth')}>
            My ending net worth
          </button>
        </span>
        <select value={heirRate} onChange={(e) => setHeirRate(Number(e.target.value))}
          aria-label="Heirs' tax bracket">
          <option value={0.12}>Heirs in the 12% bracket</option>
          <option value={0.22}>Heirs in the 22% bracket</option>
          <option value={0.24}>Heirs in the 24% bracket</option>
          <option value={0.32}>Heirs in the 32% bracket</option>
        </select>
      </div>

      {res.candidates.every((c) => c.totalConverted === 0) && (
        <div className="warnbox" style={{ marginBottom: 'var(--space-3)' }}>
          <strong>No conversion room found.</strong> In every window year ({res.windowStart}–
          {res.windowEnd}), income already fills the target brackets, so all strategies convert
          $0 and match the baseline. Retiring earlier, delaying Social Security, or lowering
          window-year income opens room.
        </div>
      )}

      {best.key !== 'none' && best.acaCollisionYears > 0 && (
        <div className="warnbox" style={{ marginBottom: 'var(--space-3)' }}>
          <strong>ACA collision:</strong> the leading strategy converts in{' '}
          {best.acaCollisionYears} pre-Medicare {best.acaCollisionYears === 1 ? 'year' : 'years'},
          where extra income shrinks or destroys the health-insurance subsidy. For early retirees
          this often dominates every other tax consideration — the numbers below already include
          the damage, but consider a rule that starts at 65.
        </div>
      )}

      <div className="heatmap-wrap">
        <table className="roth-table">
          <thead>
            <tr>
              <th>Strategy</th>
              <th>Total converted</th>
              <th>Estate value (after heirs' tax)</th>
              <th>Lifetime taxes</th>
              <th>Ending net worth</th>
              <th>Breaks even</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {all.map((c) => {
              const isBest = c.key === best.key;
              const isApplied = c.key === appliedKey;
              return (
                // The key belongs on the mapped element itself — a bare <>
                // fragment cannot carry one, so each row pair is a Fragment.
                <Fragment key={c.key}>
                  <tr className={isBest ? 'best' : ''}>
                    <td>
                      {isBest ? '★ ' : ''}{c.label}
                      {c.acaCollisionYears > 0 && (
                        <span title={`${c.acaCollisionYears} conversion years collide with ACA subsidy years`}> ⚠</span>
                      )}
                    </td>
                    <td>{c.totalConverted > 0 ? fmtCompactUsd(c.totalConverted) : '—'}</td>
                    <td>{fmtCompactUsd(c.estateValueAfterHeirTax)}</td>
                    <td>{fmtCompactUsd(c.lifetimeTaxNominal)}</td>
                    <td>{fmtCompactUsd(c.endingNetWorth)}</td>
                    <td>{c.breakEvenYear ?? (c.key === 'none' ? '—' : 'never')}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.schedule.length > 0 && (
                        <button className="btn" style={{ marginRight: 6 }}
                          onClick={() => setExpandedKey(expandedKey === c.key ? null : c.key)}>
                          {expandedKey === c.key ? 'Hide' : 'Schedule'}
                        </button>
                      )}
                      <button className="btn" disabled={isApplied} onClick={() => apply(c)}>
                        {isApplied ? 'Applied' : 'Apply'}
                      </button>
                    </td>
                  </tr>
                  {expandedKey === c.key && (
                    <tr>
                      <td colSpan={7}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', padding: '6px 0', fontSize: 'var(--fs-small)', color: 'var(--ink-secondary)' }}>
                          {c.schedule.map((s) => (
                            <span key={s.year}>
                              {s.year}: <strong>{fmtUsd(Math.round(s.amount))}</strong> at{' '}
                              {Math.round(s.marginalRate * 100)}%{s.duringAca ? ' ⚠' : ''}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="hint" style={{ marginTop: 'var(--space-3)' }}>
        ★ best under the chosen objective · ⚠ converts during ACA subsidy years · conversion
        taxes are paid from cash flow (effectively your after-tax money — the cheaper way).
        Every figure prices in IRMAA's two-year lookback, Social Security taxability, and
        capital-gains stacking. Not modeled: the pro-rata rule when you hold after-tax basis
        inside a traditional IRA — if that's you, talk to a professional before converting.
      </p>
    </section>
  );
}
